import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import MaintenancePlans from "@/pages/MaintenancePlans";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import Roofing from "@/pages/Roofing";
import Remodeling from "@/pages/Remodeling";
import PopularAustin from "@/pages/PopularAustin";
import ServiceAreas from "@/pages/ServiceAreas";
import CityDetail from "@/pages/CityDetail";
import About from "@/pages/About";
import Testimonials from "@/pages/Testimonials";
import Contact from "@/pages/Contact";
import Partner from "@/pages/Partner";
import Portfolio from "@/pages/Portfolio";
import Careers from "@/pages/Careers";
import Commercial from "@/pages/Commercial";
import ProjectListings from "@/pages/ProjectListings";
import { BlogIndex, BlogPost } from "@/pages/Blog";
import { Privacy, Terms } from "@/pages/Legal";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminOwner from "@/pages/AdminOwner";
import MemberPortal from "@/pages/MemberPortal";
import NotFound from "@/pages/NotFound";
import useAnalyticsTracker from "@/hooks/useAnalyticsTracker";
import { Toaster } from "@/components/ui/sonner";
import PublicChatWidget from "@/components/chat/PublicChatWidget";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AnalyticsTracker() {
  useAnalyticsTracker();
  return null;
}

function SiteRoutes() {
  return (
    <Routes>
      {/* Admin routes have their own layout (no main nav/footer) */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/owner/*" element={<AdminOwner />} />
      <Route path="/admin" element={<AdminDashboard />} />

      {/* Member Portal — standalone (no marketing nav/footer) */}
      <Route path="/members/*" element={<MemberPortal />} />

      {/* Marketing site routes use Layout */}
      <Route path="*" element={
        <Layout>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/maintenance-plans" element={<MaintenancePlans />} />

            {/* New canonical URL paths */}
            <Route path="/construction-services" element={<Services />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/subcontractor-partnerships" element={<Partner />} />

            {/* Legacy → canonical redirects (preserves SEO equity + old inbound links) */}
            <Route path="/services" element={<Navigate to="/construction-services" replace />} />
            <Route path="/partner" element={<Navigate to="/subcontractor-partnerships" replace />} />

            <Route path="/roofing" element={<Roofing />} />
            <Route path="/remodeling" element={<Remodeling />} />
            <Route path="/popular-austin-services" element={<PopularAustin />} />
            <Route path="/service-areas" element={<ServiceAreas />} />
            <Route path="/service-areas/:slug" element={<CityDetail />} />
            <Route path="/about" element={<About />} />
            <Route path="/testimonials" element={<Testimonials />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/commercial" element={<Commercial />} />
            <Route path="/project-listings" element={<ProjectListings />} />
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Layout>
      } />
    </Routes>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <AnalyticsTracker />
          <SiteRoutes />
          <PublicChatWidget />
          <Toaster position="top-right" richColors closeButton theme="dark" />
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}
